<template>
  <LayoutWrapper>
    <RoomGroupCanvas v-if="showCanvas" />
    <template #side>
      <div
        class="flex w-full flex-col bg-light-1 shadow dark:bg-dark-1 md:h-screen md:grow md:basis-0 lg:pt-10"
      >
        <div class="flex shrink grow-0 basis-auto flex-col">
          <div class="h-24 flex-col pl-6">
            <div class="h-6 text-sm">
              {{ selectedRoom?.number }} {{ selectedRoom?.description }}
            </div>
            <div class="flex flex-row justify-between">
              <h1 class="mb-0">
                {{ usageType }}
              </h1>
            </div>
            <BaseChip
              v-if="!selectedRoom?.isAccessible"
              text="Kein Zutritt"
              outlined
              class="mt-2"
              :icon="impedimentIconClass(selectedRoom?.typeNotAccessible)"
              :type="
                selectedRoom?.typeNotAccessible === ImpedimentType.PERMANENT
                  ? 'danger'
                  : 'warning'
              "
              data-testid="roomNotAccessibleChip"
            />
          </div>

          <BaseTabs
            :options-list="tabsOptionList"
            :selected-tab="selectedTab"
            :select-tab="selectTab"
          />
        </div>
        <div
          ref="entityList"
          class="mb-5 shrink grow basis-auto overflow-y-auto rounded-xl bg-light-1 px-4 pb-20 dark:bg-dark-1 lg:px-6"
        >
          <DeviceListForm
            v-if="selectedRoom && selectedTab === 'Geräte'"
            :parent-id="selectedRoom.id"
          />

          <PlantListForm
            v-if="selectedTab === 'Anlagen'"
            :parent-id="selectedRoom?.id"
          />

          <RadiatorListForm
            v-if="selectedRoom && selectedTab === 'Heizkörper'"
            :parent-id="selectedRoom.id"
          />
        </div>
      </div>
    </template>
    <FormDialog
      v-if="layoutStore.isFormDialogOpen"
      class="prose prose-neutral dark:prose-invert"
    />
  </LayoutWrapper>
</template>

<script lang="ts">
import { Tabs, useLayoutStore } from '@/store/layout/layoutStore';
import { useFormStore } from '@/store/form/formStore';
import FormDialog from '@/components/UI/Modal/FormDialog.vue';
import LayoutWrapper from '@/components/LayoutWrapper.vue';
import DeviceListForm from '@/components/Forms/DeviceListForm.vue';
import { computed, ref, watch } from 'vue';
import { Notification, Room } from '@/models';
import { roomTypeList } from '@/enums/room';
import RadiatorListForm from '@/components/Forms/RadiatorListForm.vue';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import BaseTabs from '@/components/UI/Tabs/BaseTabs.vue';
import { useRoomStore } from '@/store/entities/roomStore';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import PlantListForm from '@/components/Forms/PlantListFormComponents/PlantListForm.vue';
import { onMounted, provide } from 'vue';
import { useOrderStore } from '@/store/order/orderStore';
import RoomGroupCanvas from '@/components/Canvas/RoomGroupCanvas.vue';
import { impedimentIconClass, ImpedimentType } from '@/enums/generic';

export default {
  name: 'MapView',
  components: {
    RoomGroupCanvas,
    PlantListForm,
    BaseChip,
    BaseTabs,
    RadiatorListForm,
    DeviceListForm,
    LayoutWrapper,
    FormDialog,
  },
  setup() {
    const layoutStore = useLayoutStore();
    const formStore = useFormStore();
    const selectedRoom = computed(() => {
      if (!layoutStore.currentNode?.id) return undefined;
      return useRoomStore().rooms.get(layoutStore.currentNode.id);
    });

    const isScrolled = ref(false);
    provide('isScrolled', isScrolled);
    const entityList = ref<HTMLElement | undefined>(undefined);

    let tabsOptionList = [Tabs.DEVICES, Tabs.PLANTS, Tabs.RADIATORS];
    if (useOrderStore().isOnSiteInspection()) {
      tabsOptionList = [Tabs.DEVICES, Tabs.PLANTS];
    }

    onMounted(() => {
      entityList?.value?.addEventListener('scroll', checkScroll);
    });

    const checkScroll = () => {
      isScrolled.value = (entityList?.value?.scrollTop as number) > 100;
    };

    const usageType = computed(
      () =>
        roomTypeList.find((r) => r.value === selectedRoom?.value?.usageType)
          ?.name
    );
    const selectedTab = computed(() => useLayoutStore().selectedTab);
    const selectTab = (tab: Tabs) => {
      useLayoutStore().selectTab(tab);
    };

    const isSideMenuOpen = computed(() => layoutStore.isSideMenuOpen);

    watch(
      () => isSideMenuOpen.value,
      (isOpen) => {
        if (!isOpen) {
          selectTab(Tabs.DEVICES);
        }
      }
    );

    return {
      tabsOptionList,
      formStore,
      layoutStore,
      selectedRoom,
      usageType,
      selectedTab,
      isScrolled,
      entityList,
      selectTab,
      checkScroll,
    };
  },

  data() {
    return {
      showCanvas: true,
    };
  },
  computed: {
    ImpedimentType() {
      return ImpedimentType;
    },
    Notification() {
      return Notification;
    },
  },
  methods: { impedimentIconClass, useRoomStore },
};
</script>

<style scoped lang="scss">
.canvas {
  background: $canvas-background;
  transition: all 0.5s;
}
</style>
